<script setup>
import { computed } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { teamLoad } from '@/lib/capacity';
import { hasDates, formatDay, sprintStartDate } from '@/lib/dates';

const store = useRoadmapsStore();
const roadmap = computed(() => store.byId('demo-q3-platform') || store.sorted[0]);
const sprints = computed(() => Array.from({ length: roadmap.value?.settings.sprintCount || 0 }, (_, i) => i + 1));

// Same model as the app's Capacity view: time booked as a share of what each person has,
// with part-time capacity and sprints away taken into account.
const rows = computed(() =>
  roadmap.value
    ? teamLoad(roadmap.value).map(({ member, cells }) => ({ id: member.id, name: member.name, capacity: member.capacity, cells }))
    : []
);

const label = (cell) => (cell.away ? 'Away' : !cell.booked ? '' : Number.isFinite(cell.percent) ? `${cell.percent}%` : 'Over');
const sprintTitle = (n) => (roadmap.value && hasDates(roadmap.value) ? `Sprint ${n}, from ${formatDay(sprintStartDate(roadmap.value.settings, n))}` : `Sprint ${n}`);

const overbooked = computed(() => {
  for (const r of rows.value) {
    const cell = r.cells.find((c) => c.over);
    if (cell) return { name: r.name, sprint: cell.sprint, away: cell.away, percent: cell.percent };
  }
  return null;
});
</script>

<template>
  <div class="card">
    <div class="heat" :style="{ '--cols': sprints.length }">
      <span />
      <span v-for="n in sprints" :key="n" class="col" :title="sprintTitle(n)">S{{ n }}</span>
      <template v-for="row in rows" :key="row.id">
        <span class="name">{{ row.name }}<small v-if="row.capacity < 100"> {{ row.capacity }}%</small></span>
        <span v-for="c in row.cells" :key="c.sprint" class="cell" :class="[`l${c.level}`, { over: c.over, away: c.away }]">{{ label(c) }}</span>
      </template>
    </div>
    <p v-if="overbooked" class="note">
      <template v-if="overbooked.away"><strong>{{ overbooked.name }}</strong> is booked in sprint {{ overbooked.sprint }} but marked as away.</template>
      <template v-else><strong>{{ overbooked.name }}</strong> is booked for {{ overbooked.percent }}% of their time in sprint {{ overbooked.sprint }}.</template>
    </p>
    <p v-else class="note">Nobody is booked for more time than they have.</p>
  </div>
</template>

<style scoped lang="scss">
.card {
  padding: 22px;
  border: 1px solid var(--line);
  border-radius: var(--radius-l);
  background: var(--surface);
  box-shadow: 0 24px 48px -28px rgba(23, 20, 42, 0.25);
}

.heat {
  display: grid;
  grid-template-columns: minmax(84px, 120px) repeat(var(--cols), minmax(0, 1fr));
  gap: 5px;
  align-items: center;
}

.col {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-align: center;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
}

.cell {
  display: grid;
  place-items: center;
  height: 34px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  transition: background-color 0.3s, color 0.3s;

  &.l0 {
    background: var(--surface-2);
    box-shadow: inset 0 0 0 1px var(--line);
  }

  &.l1 {
    background: #e4e4ff;
    color: #4a4bd0;
  }

  &.l2 {
    background: #9a9bf2;
    color: #fff;
  }

  &.l3 {
    background: #5354dc;
    color: #fff;
  }

  &.over {
    box-shadow: inset 0 0 0 2px var(--danger);
  }

  &.away {
    background: repeating-linear-gradient(135deg, var(--surface-2) 0 5px, var(--bg) 5px 10px);
    color: var(--text-3);
  }

  &.away.over {
    color: var(--danger);
    box-shadow: inset 0 0 0 2px var(--danger);
  }
}

.name small {
  margin-left: 4px;
  font-size: 10px;
  font-weight: 500;
  color: var(--text-3);
}

.note {
  margin-top: 14px;
  font-size: 13px;
  color: var(--text-2);

  strong {
    color: var(--text);
    font-weight: 600;
  }
}
</style>
